<template>
  <section>
    <div
      v-if="(isTextEntry(quiz) || isEssayType) && answerValue"
      class="quiz-result__entered-answer container-sm"
    >
      <div class="quiz-result__entered-answer-title">
        {{ $t('common.modal.preview.entered_answer') }}
      </div>
      <div class="quiz-result__entered-answer-value">
        {{ answerValue }}
      </div>
    </div>

    <div class="quiz-result" v-if="isAssigneeView && isSolvedQuiz">
      <div class="container-sm">
        <div class="quiz-result__content row" v-if="pageType !== 'essay_type'">
          <div class="col-6 quiz-result-answer">
            <template v-if="isCorrect">
              <img
                src="@/assets/images/quiz-correct-s.svg"
                class="quiz-correct-s inline-block mr-6"
              />
              <strong class="quiz-result__label">
                {{ $t('viewer.page.quiz.result.correct') }}
              </strong>
            </template>
            <template v-if="!isCorrect">
              <img
                src="@/assets/images/quiz-wrong-s.svg"
                class="quiz-wrong-s inline-block mr-6"
              />
              <strong class="quiz-result__label">
                {{ $t('viewer.page.quiz.result.wrong') }}
              </strong>
            </template>
          </div>
          <div
            v-if="isMultipleChoice(quiz)"
            class="col-6 quiz-result__description"
          >
            <span
              class="quiz-result__selected"
              :class="quizResultSelected(isCorrect)"
            >
              <span>
                {{ $t('common.modal.preview.chosen_answer') }}
                {{
                  $t('common.modal.preview.answer_number', {
                    n: selectedOptionIndex,
                  })
                }}
              </span>
            </span>
            <span class="quiz-result__answer">
              {{ $t('common.modal.preview.correct_answer') }}
              <span>
                {{
                  $t('common.modal.preview.answer_number', {
                    n: correctOptionIndices[0],
                  })
                }}
              </span>
            </span>
          </div>
        </div>

        <div class="quiz-result__content" v-else>
          <div class="row quiz-result__score">
            <div class="col-12 quiz-result__value-container">
              <strong
                class="quiz-result__label typography-font-weight-600"
                v-if="!gradingStatus"
              >
                {{ $t('viewer.quiz.result.score_labels.not_graded') }}
              </strong>

              <template v-else>
                <strong
                  class="
                    quiz-result__essay-type-label
                    typography-font-weight-600
                  "
                >
                  {{ $t('viewer.page.completed.graded') }}
                </strong>
                <span class="quiz-result__value">
                  <strong>{{ currentPageResult.points }}</strong
                  >/{{ $t('common.numeric_score', { n: quiz.score }) }}
                </span>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="quiz-result divider" v-else/>

    <div
      v-if="isTextEntry(quiz)"
      class="quiz-result__answer-content container-sm"
    >
      <div class="quiz-result__answer-content-title">
        {{ $t('common.modal.preview.correct_answer') }}
      </div>
      <div>
        <span class="quiz-result__answer-content-value"
              v-for="(correctOptionContent, index) in correctOptionContents"
              v-bind:key="index"
        >
          {{ correctOptionContent }}
          <span v-if="correctOptionContents.length > (index + 1)">
          ,
          </span>
        </span>
      </div>
    </div>
  </section>
</template>

<script>
import { orderBy, isEmpty, get } from 'lodash';

import quiz from '@/mixins/quiz';
import { mapGetters } from 'vuex';

export default {
  name: 'new-class-preview-quiz-result',

  mixins: [quiz],

  data() {
    return {
      options: null,
    };
  },

  props: {
    quiz: {
      type: Object,
      default: () => {
      },
    },
    quizResult: {
      type: Object,
      default: () => {
      },
    },
    isAssigneeView: {
      type: Boolean,
      default: false,
    },
    isSolvedQuiz: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters({
      routeParams: 'getRouteParams',
      preSelectedOption: 'getPreSelectedOptionForNewClass',
      preEnteredValue: 'getPreEnteredValueForNewClass',
      gradingStatus: 'getGradingStatusForNewClass',
      pageResults: 'getPagesResultForNewClass',
    }),

    currentPageResult() {
      return this.pageResults[this.quiz.id];
    },

    pageType() {
      return get(this.quiz, 'page_type.name', '');
    },

    isEssayType() {
      return this.pageType === 'essay_type';
    },

    quizOptions() {
      return orderBy(this.options, ['id'], ['asc']);
    },

    correctAnswerOptions() {
      const answerOptions = this.quizOptions.filter(option => option.is_correct);
      if (!isEmpty(answerOptions)) {
        return answerOptions;
      }
      return [];
    },

    correctOptionIndices() {
      const indices = [];
      this.quizOptions.forEach((option, index) => {
        this.correctAnswerOptions.forEach((correctOptions) => {
          if (correctOptions.id === option.id) {
            indices.push(index + 1);
          }
        });
      });

      return indices;
    },

    selectedOptionIndex() {
      if (isEmpty(this.preSelectedOption)) {
        return 0;
      }

      return this.preSelectedOption.option_index;
    },

    correctOptionContents() {
      if (isEmpty(this.correctAnswerOptions)) {
        return null;
      }

      return this.correctAnswerOptions.map(option => option.content || option.value);
    },

    answerValue() {
      if (isEmpty(this.preEnteredValue)) return null;
      return this.preEnteredValue;
    },

    isCorrect() {
      if (this.pageType === 'multiple_choice') {
        return this.correctOptionIndices.includes(this.selectedOptionIndex);
      }
      if (this.pageType === 'text_entry') {
        return this.correctOptionContents.includes(this.answerValue);
      }
      return false;
    },
  },

  methods: {
    quizResultSelected(isCorrect) {
      return isCorrect ? '' : 'active';
    },
  },

  beforeMount() {
    if (this.routeParams.cardType === 'store-cards') {
      this.options = this.quiz.options;
    } else if (this.routeParams.cardType === 'cards') {
      this.options = this.quiz.answers;
    }
  },

  watch: {
    quiz() {
      if (this.routeParams.cardType === 'store-cards') {
        this.options = this.quiz.options;
      } else if (this.routeParams.cardType === 'cards') {
        this.options = this.quiz.answers;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/components/_typographies";
@import "@/assets/styles/components/_breakpoints";

.quiz-result {
  background-color: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-style: solid none;
}

.divider {
  height: 40px;
}

.quiz-result__content {
  max-width: 570px;
  margin: 0 auto;
  padding: 20px 0;
  position: relative;

  .quiz-correct-s,
  .quiz-wrong-s {
    width: 24px;
    height: 24px;
  }
}

.quiz-result__value-container {
  display: flex;
  justify-content: space-between;
}

.quiz-result__score {
  display: block;
}

.quiz-result__label {
  font-size: 17px;
  font-weight: 400;

  @media (min-width: $desktop-breakpoint) {
    font-size: 20px;
  }
}

.quiz-result-answer {
  display: flex;
  align-items: center;
  padding: 0;
}

.quiz-result__description {
  line-height: 2;
  margin: 0;
  padding: 0;
  text-align: right;
  font-size: 14px;
  line-height: 1.43;

  @media (min-width: $desktop-breakpoint) {
    font-size: 18px;
  }

  > .quiz-result__selected {
    display: inline-block;
    padding-left: 12px;
  }

  .active {
    color: #ff595f;
  }

  > .quiz-result__answer {
    display: inline-block;
    padding-left: 12px;
    color: #03a9f4;
  }
}

.quiz-result__entered-answer {
  padding: 16px;

  .quiz-result__entered-answer-title {
    font-size: 14px;
    font-weight: bold;
    color: rgba(0, 0, 0, 0.54);
    padding: 6px 0px 6px 0px;
  }

  .quiz-result__entered-answer-value {
    background-color: #f5f5f5;
    border: solid 1px #eeeeee;
    border-radius: 4px;
    padding: 14px;
    font-size: 14px;
  }
}

.quiz-result__answer-content {
  padding: 14px;

  .quiz-result__answer-content-title {
    font-size: 14px;
    font-weight: bold;
    color: #03a9f4;
    padding: 6px 0px 6px 0px;

    &.user-value {
      color: #000;
    }
  }
}
</style>
